import {
  Button,
  ButtonGroup,
  DialogActionTrigger,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { FaExchangeAlt } from "react-icons/fa";

import { type OpenSearchDoc, TableRowsService } from "@/client";
import useCustomToast from "@/hooks/useCustomToast";
import { handleError } from "@/utils";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Field } from "../ui/field";

interface TableField {
  field_id: string;
  field_name: string;
  field_type: string;
  is_required: boolean;
}

// 테이블 행 데이터 타입 정의
type TableRowData = Record<string, string | number>;

interface EditTableRowProps {
  tableId: string;
  openSearchDoc: OpenSearchDoc;
}

const EditTableRow = ({ tableId, openSearchDoc }: EditTableRowProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();
  const { showSuccessToast } = useCustomToast();

  // ✅ Load metadata (fields)
  const { data: fieldMeta, isLoading: isMetaLoading } = useQuery({
    queryKey: ["openSearchDocs", tableId],
    queryFn: () =>
      TableRowsService.readTableRows({ tableId, skip: 0, limit: 1 }),
    select: (res) => res.fields as unknown as TableField[],
    enabled: isOpen,
  });

  // ✅ Form state
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TableRowData>({
    mode: "onBlur",
    criteriaMode: "all",
  });

  // ✅ Set default values when opening
  const onDialogOpen = () => {
    setIsOpen(true);
    if (openSearchDoc?.data) {
      reset(openSearchDoc.data as TableRowData);
    }
  };

  const mutation = useMutation({
    mutationFn: (data: TableRowData) =>
      TableRowsService.updateTableRow({
        tableId,
        tableRowId: openSearchDoc.id,
        requestBody: { data },
      }),
    onSuccess: () => {
      showSuccessToast("Row updated successfully.");
      setIsOpen(false);
    },
    onError: handleError,
    onSettled: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "openSearchDocs" &&
          query.queryKey[1] !== null &&
          typeof query.queryKey[1] === "object" &&
          "tableId" in query.queryKey[1] &&
          query.queryKey[1].tableId === tableId,
      });
    },
  });

  const onSubmit: SubmitHandler<TableRowData> = (data) => {
    mutation.mutate(data);
  };

  return (
    <DialogRoot
      size={{ base: "xs", md: "md" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => {
        setIsOpen(open);
        if (open) onDialogOpen();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost">
          <FaExchangeAlt fontSize="16px" />
          데이터 수정
        </Button>
      </DialogTrigger>

      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>데이터 수정</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text mb={4}>아래의 데이터를 수정하세요.</Text>
            <VStack gap={4}>
              {!isMetaLoading &&
                (fieldMeta as TableField[] | undefined)?.map((field) => (
                  <Field
                    key={field.field_id}
                    label={field.field_name}
                    required={field.is_required}
                    invalid={!!errors[field.field_name]}
                    errorText={
                      errors[field.field_name]?.message?.toString() ?? ""
                    }
                  >
                    <Input
                      placeholder={field.field_name}
                      type={field.field_type === "number" ? "number" : "text"}
                      {...register(field.field_name, {
                        required: field.is_required
                          ? `${field.field_name} is required`
                          : false,
                        valueAsNumber: field.field_type === "number",
                      })}
                    />
                  </Field>
                ))}
            </VStack>
          </DialogBody>

          <DialogFooter gap={2}>
            <ButtonGroup>
              <DialogActionTrigger asChild>
                <Button
                  variant="subtle"
                  colorPalette="gray"
                  disabled={isSubmitting}
                >
                  취소
                </Button>
              </DialogActionTrigger>
              <Button type="submit" variant="solid" loading={isSubmitting}>
                저장
              </Button>
            </ButtonGroup>
          </DialogFooter>
        </form>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  );
};

export default EditTableRow;
